import { useRef } from "react";

import { Inbox, Paperclip } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { UsePaginationReturn } from "@/hooks/use-pagination";
import type { PaginationMeta } from "@/types/common";
import type { ContestSubmissionListItem } from "@/types/contest-submission";

import { ContestCharCountCell } from "./contest-char-count-cell";
import { ContestChecklistBadges } from "./contest-checklist-badges";
import { ContestScreeningBadge } from "./contest-screening-badge";
import { ContestSubmissionPagination } from "./contest-submission-pagination";
import { formatSubmittedAt } from "./detail/format";

interface Props {
  data: ContestSubmissionListItem[];
  meta: PaginationMeta;
  pagination: UsePaginationReturn;
  onOpen: (item: ContestSubmissionListItem) => void;
  /** 행 hover 시 상세 prefetch (선택) */
  onPrefetch?: (item: ContestSubmissionListItem) => void;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: (ids: string[]) => void;
}

function formatBirthdate(raw: string | null): string {
  if (!raw) return "-";
  if (/^\d{8}$/.test(raw)) {
    return `${raw.slice(0, 4)}.${raw.slice(4, 6)}.${raw.slice(6, 8)}`;
  }
  return raw;
}

export function ContestSubmissionTable({
  data,
  meta,
  pagination,
  onOpen,
  onPrefetch,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}: Props) {
  // hover prefetch 는 150ms 머문 행만 발동 — 마우스가 테이블을 스치기만 해도
  // 행마다 요청이 나가 admin rate limit(분당 요청 수)을 갉아먹는 것을 방지.
  const prefetchTimer = useRef<number | null>(null);
  const schedulePrefetch = (item: ContestSubmissionListItem) => {
    if (!onPrefetch) return;
    if (prefetchTimer.current !== null) window.clearTimeout(prefetchTimer.current);
    prefetchTimer.current = window.setTimeout(() => onPrefetch(item), 150);
  };
  const cancelPrefetch = () => {
    if (prefetchTimer.current !== null) window.clearTimeout(prefetchTimer.current);
    prefetchTimer.current = null;
  };
  if (data.length === 0) {
    return (
      <div className="rounded-lg border p-12 text-center space-y-3">
        <Inbox className="mx-auto h-10 w-10 text-muted-foreground" />
        <p className="text-muted-foreground">조건에 맞는 응모 이력이 없습니다.</p>
      </div>
    );
  }

  const pageIds = data.map((item) => item._id);
  const allSelected = pageIds.every((id) => selectedIds.has(id));

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={() => onToggleSelectAll(pageIds)}
                  aria-label="현재 페이지 전체 선택"
                />
              </TableHead>
              <TableHead className="w-[150px]">제출 일시</TableHead>
              <TableHead className="w-[120px]">응모자</TableHead>
              <TableHead className="w-[100px]">필명</TableHead>
              <TableHead className="w-[110px]">생년월일</TableHead>
              <TableHead className="w-[130px]">휴대전화</TableHead>
              <TableHead>작품 제목</TableHead>
              <TableHead className="w-[100px] text-right">글자수</TableHead>
              <TableHead className="w-[80px] text-center">심사</TableHead>
              <TableHead className="w-[160px]">부적격 사유</TableHead>
              <TableHead className="w-[80px] text-center">원고</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item) => (
              <TableRow
                key={item._id}
                className="cursor-pointer hover:bg-muted/60"
                onClick={() => onOpen(item)}
                onMouseEnter={() => schedulePrefetch(item)}
                onMouseLeave={cancelPrefetch}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpen(item);
                  }
                }}
              >
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedIds.has(item._id)}
                    onCheckedChange={() => onToggleSelect(item._id)}
                    aria-label={`${item.workTitle ?? item._id} 선택`}
                  />
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatSubmittedAt(item)}
                </TableCell>
                <TableCell className="text-sm">
                  <div className="font-medium">{item.name || "-"}</div>
                  <div className="text-xs text-muted-foreground truncate max-w-[120px]">
                    {item.email || item.userEmail}
                  </div>
                </TableCell>
                <TableCell className="text-sm">{item.penName || "-"}</TableCell>
                <TableCell className="text-sm">{formatBirthdate(item.birthdate)}</TableCell>
                <TableCell className="text-sm">{item.phone || "-"}</TableCell>
                <TableCell className="text-sm font-medium max-w-[240px] truncate">
                  {item.workTitle || "-"}
                </TableCell>
                <TableCell className="text-right">
                  <ContestCharCountCell charCount={item.charCount} />
                </TableCell>
                <TableCell className="text-center">
                  <ContestScreeningBadge screening={item.screening} />
                </TableCell>
                <TableCell>
                  <ContestChecklistBadges
                    checklist={item.screening?.checklist ?? null}
                  />
                </TableCell>
                <TableCell className="text-center">
                  {item.manuscript ? (
                    <Badge variant="secondary" className="gap-1">
                      <Paperclip className="h-3 w-3" />
                      첨부
                    </Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">없음</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <ContestSubmissionPagination meta={meta} pagination={pagination} />
    </div>
  );
}
